// Recovers from "stale chunk" errors after a production deploy: when a user
// loaded the previous index.html and then navigates to a lazy route whose
// hashed chunk no longer exists, force one full reload so they pick up the new
// bundle. The sessionStorage flag prevents an infinite reload loop if the
// import keeps failing for some other reason.
const RELOAD_FLAG = 'wc-chunk-reload';
const OVERLAY_ID = 'wc-reload-overlay';
const LOG_PREFIX = '[chunk-reload]';

const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

function getReloadTarget(): string {
  return window.location.pathname + window.location.search + window.location.hash;
}

// Minimal overlay so the page doesn't go blank during the reload tick.
function showReloadOverlay(): void {
  if (typeof document === 'undefined' || !document.body) {
    return;
  }
  if (document.getElementById(OVERLAY_ID)) {
    return;
  }
  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.setAttribute('role', 'status');
  overlay.setAttribute('aria-live', 'polite');
  overlay.innerHTML = `
    <style>
      @keyframes wc-reload-spin { to { transform: rotate(360deg); } }
      #${OVERLAY_ID} {
        position: fixed;
        inset: 0;
        z-index: 2147483647;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--wc-surface, #000017);
        color: var(--wc-white, #FFFFFF);
      }
      #${OVERLAY_ID} > .wc-reload-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid var(--wc-white-15, rgba(255, 255, 255, 0.15));
        border-top-color: var(--wc-white-70, rgba(255, 255, 255, 0.70));
        border-radius: 50%;
        animation: wc-reload-spin 0.8s linear infinite;
      }
      @media (prefers-reduced-motion: reduce) {
        #${OVERLAY_ID} > .wc-reload-spinner { animation: none; }
      }
    </style>
    <div class="wc-reload-spinner" aria-hidden="true"></div>
  `;
  document.body.appendChild(overlay);
}

function tryRecoverFromChunkError(message: string, source: string): boolean {
  if (!CHUNK_ERROR_RE.test(message)) {
    return false;
  }
  if (sessionStorage.getItem(RELOAD_FLAG)) {
    console.warn(LOG_PREFIX, 'loop guard hit, surfacing real error', {
      source,
      message,
    });
    return false;
  }
  console.info(LOG_PREFIX, 'chunk error matched, starting recovery', {
    source,
    message,
  });
  sessionStorage.setItem(RELOAD_FLAG, '1');
  showReloadOverlay();
  const target = getReloadTarget();
  // Small delay so the overlay paints before navigation tears down the page.
  setTimeout(() => {
    window.location.assign(target);
  }, 200);
  return true;
}

export function lazyImport<T>(load: () => Promise<T>): Promise<T> {
  return load().catch((err: unknown) => {
    const message = err instanceof Error ? err.message : String(err);
    if (tryRecoverFromChunkError(message, 'lazyImport')) {
      return new Promise<T>(() => {});
    }
    throw err;
  });
}

// Catches dynamic imports that aren't routed through lazyImport — e.g. an
// `await import('./heavy-thing')` inside a route component. Listens for both
// unhandledrejection (from awaited/unhandled imports) and error (from
// <script> / <link rel=modulepreload> failures during preload).
export function installChunkErrorHandler(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason ?? '');
    if (tryRecoverFromChunkError(message, 'unhandledrejection')) {
      event.preventDefault();
    }
  });
  window.addEventListener(
    'error',
    (event) => {
      const message = event.message ?? '';
      if (tryRecoverFromChunkError(message, 'error')) {
        event.preventDefault();
      }
    },
    // Capture phase — module preload failures fire on the <link>/<script>
    // element and don't bubble.
    true,
  );
}

export function clearChunkReloadFlag(): void {
  sessionStorage.removeItem(RELOAD_FLAG);
}
